// AbilityDisplay.tsx - Non-interactive version of AbilityButton
import React, { useContext } from 'react';
import './AbilityDisplay.css';
import { SimplifiedSpell } from '../../../../../gameTypes';
import GameAssetsContext from '../../../../../context/GameAssetContext';

interface AbilityDisplayProps {
  abilityKey: string;
  abilityImage: string;
  spell: SimplifiedSpell;
  isAutoAttack?: boolean;
  compactMode?: boolean;
  onAbilityClick?: (abilityKey: string) => void;
}

const AbilityDisplay: React.FC<AbilityDisplayProps> = ({
  abilityKey,
  abilityImage,
  spell,
  isAutoAttack = false,
  compactMode = false,
  onAbilityClick,
}) => {
  const { gameWideAssets } = useContext(GameAssetsContext);
  
  // Safety check for spell data
  if (!spell) {
    console.error(`AbilityDisplay - No spell data for ability ${abilityKey}`);
    return (
      <div className="ability-display-container">
        <div className="ability-display error">
          <div style={{ color: 'red', fontSize: '10px' }}>No Data</div>
        </div>
      </div>
    );
  }

  const {
    remainingCooldown = 0,
    isOnCooldown = false,
    pointsInSpell = 0,
    timer,
    secondaryCDRemaining,
    secondaryCDisOnCooldown,
    currentCharges,
    maxCharges,
    cooldown = 0,
    range = 0,
  } = spell;

  let displayCooldownText = "";
  let finalGreyedOut = false; // flag for greying out the ability image

  // If secondary cooldown info is available (i.e. charges exist)
  if (
    secondaryCDRemaining !== undefined &&
    currentCharges !== undefined &&
    maxCharges !== undefined
  ) {
    if (currentCharges >= maxCharges) {
      // At max charges: no cooldown text, image is not greyed out.
      displayCooldownText = "";
      finalGreyedOut = false;
    } else {
      if (currentCharges === 0) {
        // No charges available: show primary cooldown regardless of secondary cooldown state.
        if (isOnCooldown) {
          displayCooldownText = remainingCooldown.toFixed(1) + "s";
          finalGreyedOut = true;
        }
      } else {
        // There is at least one charge available but not max.
        if (secondaryCDisOnCooldown) {
          // Secondary cooldown is active: show its remaining time and grey out.
          displayCooldownText = secondaryCDRemaining.toFixed(1) + "s";
          finalGreyedOut = true;
        } else {
          // Secondary cooldown not active: show ability normally.
          displayCooldownText = "";
          finalGreyedOut = false;
        }
      }
    }
  } else {
    // Fallback if no secondary cooldown info is available.
    if (isOnCooldown) {
      displayCooldownText = remainingCooldown.toFixed(1) + "s";
      finalGreyedOut = true;
    } else if (timer !== undefined && timer > 0) {
      displayCooldownText = timer.toFixed(1) + "s";
      finalGreyedOut = false;
    }
  }

  // Grey out if on cooldown OR if no points in spell (except for passive and auto attack)
  const shouldGreyOut = finalGreyedOut || (pointsInSpell === 0 && !isAutoAttack && abilityKey !== 'P');
  const isGreyedOutClass = shouldGreyOut ? 'greyed-out' : '';
  const isPassiveClass = abilityKey === 'P' ? 'passive' : '';

  const handleClick = () => {
    if (onAbilityClick && abilityKey !== 'P') { // Don't allow clicking on passive
      onAbilityClick(abilityKey);
    }
  };

  return (
    <div className="ability-display-container">
      <div
        className={`ability-display ${isGreyedOutClass} ${isPassiveClass} ${
          timer !== undefined && timer > 0 ? 'active-timer' : ''
        } ${onAbilityClick && abilityKey !== 'P' ? 'clickable' : ''}`}
        onClick={handleClick}
        style={{ cursor: onAbilityClick && abilityKey !== 'P' ? 'pointer' : 'default' }}
      >
        <img
          className="ability-img"
          src={abilityImage}
          alt={`Ability ${abilityKey}`}
        />
        <div className="ability-letter">{abilityKey.toUpperCase()}</div>
        {!isAutoAttack && (
          <>
            <div className="ability-points">{pointsInSpell}</div>
{/*             <div className="ability-mana-cost">{mana}</div>
 */}            {displayCooldownText && (
              <div className="ability-cooldown">{displayCooldownText}</div>
            )}
          </>
        )}
      </div>
      {!isAutoAttack && !compactMode && (
        <div className={`ability-extra-info ${shouldGreyOut ? 'greyed-out' : ''}`}>
          <div className="ability-base-cooldown">
            <img 
              src={gameWideAssets.abilityHasteIcon} 
              alt="Cooldown" 
              className="ability-info-icon"
            />
            {cooldown.toFixed(1)}s
          </div>
          <div className="ability-range">
            <img 
              src={gameWideAssets.attackRangeIcon} 
              alt="Range" 
              className="ability-info-icon"
            />
            {range === -1 ? "Global" : range}
          </div>
        </div>
      )}
    </div>
  );
};

export default AbilityDisplay; 